import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import IngredientCategory from 'classes/StandingData/IngredientCategory/Search/IngredientCategory';
import IngredientCategoryList from 'classes/StandingData/IngredientCategory/Search/IngredientCategoryList';
import Search from 'classes/StandingData/IngredientCategory/Search/Search';
import IngredientCategoryDetailComponent from 'components/desktop/StandingData/IngredientCategory/Detail/IngredientCategoryDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const IngredientCategoryManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [ingredientCategoryList, setIngredientCategoryList] = useState<IngredientCategoryList>(
		new IngredientCategoryList()
	);
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.description.value ?? '');

	const [displayOverlay, setDisplayOverlay] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			if (search.id.value) setDisplayOverlay(true);
			await getIngredientCategories();
		} else {
			await getIngredientCategories();
		}
		setSearch(clone(search));
	};

	const getIngredientCategories = async () => {
		setIsLoading(true);
		try {
			const response = await ingredientCategoryList.callApi(search.mapToRequest());
			response.do((x) => {
				ingredientCategoryList.mapFromApi(x);
				setIngredientCategoryList(clone(ingredientCategoryList));
			});
		} finally {
			setIsLoading(false);
		}
	};

	const getSuggestedIngredientCategories = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await ingredientCategoryList.search(searchTerm, props.archive ?? false);
		} else {
			ingredientCategoryList.filtered = [];
		}

		setIngredientCategoryList(clone(ingredientCategoryList));
		return ingredientCategoryList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getIngredientCategories();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		setDisplayOverlay(true);
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		setDisplayOverlay(false);
		handleChangeId(null);
	};

	const handleArchiveIngredientCategory = async (
		ingredientCategory: IngredientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:INGREDIENTCATEGORY_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await ingredientCategory.callApiDelete();
				await Swal.fire({
					title: t('standingData:INGREDIENTCATEGORY_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getIngredientCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreIngredientCategory = async (
		ingredientCategory: IngredientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:INGREDIENTCATEGORY_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await ingredientCategory.callApiRestore();
				await Swal.fire({
					title: t('standingData:INGREDIENTCATEGORY_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getIngredientCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
		setDisplayOverlay(true);
	};

	const getButtonDataArchive = (input: IngredientCategory): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveIngredientCategory(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: IngredientCategory): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreIngredientCategory(input),
			};
		}
	};

	const renderButtons = (input: IngredientCategory): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return ingredientCategoryList.all.map((ingredientCategory: IngredientCategory) => {
			return {
				id: ingredientCategory.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div className="d-flex flex-row" style={{ width: '300px' }}>
							{ingredientCategory.name}
						</div>
						{ingredientCategory.parentName ? (
							<>
								<div style={{ width: '300px' }}>
									<span className="grey">
										{t('_general:NUTRI_SCORE_CATEGORY')}:{' '}
									</span>
									{ingredientCategory.nutriScoreCategory}
								</div>
								<div className="flex-fill">
									<span className="grey">{t('standingData:PARENT')}: </span>
									{ingredientCategory.parentName}
								</div>
							</>
						) : (
							<div className="flex-fill">
								<span className="grey">{t('_general:NUTRI_SCORE_CATEGORY')}: </span>
								{ingredientCategory.nutriScoreCategory}
							</div>
						)}
						<div>{renderButtons(ingredientCategory)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:INGREDIENTCATEGORY_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_INGREDIENTCATEGORY')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:INGREDIENTCATEGORY_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedIngredientCategories}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>
						{ingredientCategoryList.totalCount +
							' ' +
							t('_general:INGREDIENTCATEGORIES')}
					</p>
					<StandingDataList
						archive={props.archive}
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<IngredientCategoryDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={ingredientCategoryList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default IngredientCategoryManagementComponent;
